import { useCallback, useMemo, useState } from 'react'
import { AlertTriangle, Download, Gavel, Layers, Loader2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, BulkAction, Selection } from '@/components/DataTable/types'
import { fetchUsers, issueStrike, type AdminUserRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { downloadCsv } from '@/lib/csv'
import { ListBackLink, Body, ActionCard } from './userActionKit'
import { USERS_STR as S } from './usersAdminApi'
import { sendUserWarning } from './userWarningsApi'

type BulkKind = 'warn' | 'strike'

function nameOf(r: AdminUserRow): string {
  return (
    [r.first_name, r.last_name].filter(Boolean).join(' ') ||
    (r.username ? `@${r.username}` : `#${r.id}`)
  )
}

const baseColumns: AdminColumn<AdminUserRow>[] = [
  { key: 'id', header: 'ID', type: 'id', width: 90 },
  {
    key: 'name',
    header: 'Ism',
    type: 'text',
    minWidth: 160,
    sortable: false,
    accessor: (r) => [r.first_name, r.last_name].filter(Boolean).join(' '),
  },
  { key: 'username', header: 'Username', type: 'text', width: 150 },
  {
    key: 'status',
    header: 'Holat',
    type: 'enum',
    width: 120,
    options: [
      { value: 'active', label: 'Aktiv', status: 'active' },
      { value: 'banned', label: 'Bloklangan', status: 'banned' },
    ],
  },
]

/**
 * A3.04 — Ommaviy amallar (bulk). Belgilangan qatorlarga KETMA-KET real amal:
 * ommaviy ogohlantirish (`POST /admin/users/:id/warn`) yoki ommaviy 1-daraja
 * strayk (`POST /admin/users/:id/strike`). «Butun filtr bo'yicha» rejimи (mode
 * `allMatching`) DESTRUKTIV amal uchun ATAYLAB bloklangan (tables.md four-eyes) —
 * faqat aniq tanlangan qatorlar. Har chaqiruv serverда auditlanadi.
 */
export function BulkUserActions() {
  const { toast } = useToast()
  const [pending, setPending] = useState<{ kind: BulkKind; rows: AdminUserRow[] } | null>(null)
  const [text, setText] = useState('')
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const [reloadKey, setReloadKey] = useState(0)

  const onBulk = useCallback(
    (kind: BulkKind) => (sel: Selection<AdminUserRow>) => {
      if (sel.mode !== 'rows' || sel.rows.length === 0) {
        toast(S.bulkOnlyRows, { tone: 'warning' })
        return
      }
      setText('')
      setProgress(0)
      setPending({ kind, rows: sel.rows })
    },
    [toast],
  )

  const bulkActions = useMemo<BulkAction<AdminUserRow>[]>(
    () => [
      {
        id: 'warn',
        label: S.bulkWarn,
        icon: <AlertTriangle size={13} />,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: onBulk('warn'),
      },
      {
        id: 'strike',
        label: S.bulkStrike,
        icon: <Gavel size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: onBulk('strike'),
      },
    ],
    [onBulk],
  )

  const fetcher = useCallback(fetchUsers, [])

  const execute = async () => {
    if (!pending || !text.trim()) return
    setRunning(true)
    let ok = 0
    let fail = 0
    for (const r of pending.rows) {
      try {
        if (pending.kind === 'warn') await sendUserWarning(r.id, text.trim())
        else await issueStrike(r.id, 1, text.trim())
        ok++
      } catch {
        fail++
      }
      setProgress(ok + fail)
    }
    setRunning(false)
    setPending(null)
    setReloadKey((k) => k + 1)
    toast(S.bulkResult(ok, fail), { tone: fail === 0 ? 'success' : 'warning' })
  }

  return (
    <>
      <PageHeader title={S.bulkTitle} screenId="A3.04" actions={<ListBackLink />} />
      <DataTable<AdminUserRow>
        key={reloadKey}
        screenId="A3.04"
        permission={PERMISSIONS.USERS_VIEW}
        columns={baseColumns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        selectable
        bulkActions={bulkActions}
        exportable
        emptyState={{ icon: <Layers size={28} strokeWidth={1.5} />, description: S.bulkHint }}
      />

      {pending ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg border border-border-default bg-surface-1 p-4 shadow-xl">
            <div className="mb-2 text-sm font-medium text-primary">
              {pending.kind === 'warn' ? S.bulkWarn : S.bulkStrike} · {pending.rows.length}
            </div>
            <div className="mb-3 max-h-24 overflow-auto rounded-md border border-border-subtle bg-surface-2 px-2 py-1.5 text-xs text-tertiary">
              {pending.rows.map((r) => nameOf(r)).join(', ')}
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={pending.kind === 'warn' ? S.messagePh : S.reasonPh}
              rows={3}
              maxLength={2000}
              className="w-full resize-y rounded-md border border-border-default bg-surface-2 px-3 py-2 text-sm text-primary outline-none focus:border-border-strong"
            />
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                onClick={() => setPending(null)}
                disabled={running}
                className="rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-secondary hover:text-primary disabled:opacity-50"
              >
                Bekor
              </button>
              <button
                onClick={() => void execute()}
                disabled={running || !text.trim()}
                className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-3 py-1.5 text-sm text-danger hover:bg-danger hover:text-white disabled:opacity-50"
              >
                {running ? <Loader2 size={14} className="animate-spin" /> : null}
                {running ? S.bulkRunning(progress) : S.apply}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

/**
 * A3.05 — Eksport (CSV). JONLI `GET /admin/users` dan joriy ro'yxatni olib
 * UTF-8-BOM CSV faylга yozadi (Excel kirill/lotinни to'g'ri ochsin). Yirik
 * eksport four-eyes/audit talab qiladi (serverда majburlanadi).
 */
export function UserExport() {
  const { toast } = useToast()
  const [limit, setLimit] = useState(200)
  const [busy, setBusy] = useState(false)

  const run = async () => {
    setBusy(true)
    const ac = new AbortController()
    try {
      const res = await fetchUsers({
        filters: [],
        sort: [],
        page: 1,
        pageSize: limit,
        search: '',
        signal: ac.signal,
      })
      const table: (string | number)[][] = [
        ['id', 'first_name', 'last_name', 'username', 'phone', 'status', 'created_at'],
        ...res.rows.map((r) => [
          r.id,
          r.first_name ?? '',
          r.last_name ?? '',
          r.username ?? '',
          r.phone ?? '',
          r.status ?? '',
          typeof r.created_at === 'number' ? new Date(r.created_at).toISOString() : String(r.created_at ?? ''),
        ]),
      ]
      downloadCsv('grom-users', table)
      toast(S.done, { tone: 'success' })
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.exportTitle} screenId="A3.05" actions={<ListBackLink />} />
      <Body>
        <ActionCard hint={S.exportHint}>
          <label className="block">
            <span className="mb-1 block text-xs text-tertiary">{S.exportLimit}</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
            >
              {[50, 100, 200, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={() => void run()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {busy ? S.exporting : S.exportBtn}
          </button>
        </ActionCard>
      </Body>
    </>
  )
}
